import Vue from 'vue';
import Component from 'vue-class-component';
import Fragment from 'vue-fragment';
import '@gig/magic-message-bus';
import '@gig/magic-ab-test';

// Uncomment for message bus logging
// window.eventBus.enableLogging();
// window.queryBus.enableLogging();
// window.commandBus.enableLogging();

import { magic } from '@gig/magic-sdk';
import {
    links,
    initializeTheme,
    registerTrackingService,
    languageQuery,
} from '@gig/magic-common';
import { TrackingServiceV2 } from '@gig/magic-common/dist/services/tracking/tracking-v2.service';

import { App } from './components/app';
import { config, setHtmlLang } from './services';
import { getRouter } from './routes/router';
import { store } from './store';
import { initIncompleteSSORegistration } from './services/sso/incomplete-sso-registration';
import {
    CountryToJurisdiction,
    i18nQuery,
    toolkit,
    MobileAppMessage,
    MobileAppMessageTypes,
    MobileAppNavigateToMessage,
} from '@gig/magic-frontend-toolkit';
import { Jurisdiction } from '@gig/magic-jurisdictions';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';

Vue.config.productionTip = false;
Vue.config.ignoredElements = ['shady-slot', 'slot', /^magic-/, /^ft-/];

export const COUNTRY_TO_JURISDICTION: CountryToJurisdiction = {
    [Country.UNITED_KINGDOM]: Jurisdiction.UKGC,
    [Country.MALTA]: Jurisdiction.MGA,
};

const defaultJurisdiction = Jurisdiction.UKGC;

// Register the router hooks with their names
Component.registerHooks([
    'beforeRouteEnter',
    'beforeRouteLeave',
    'beforeRouteUpdate',
]);

// If we're running inside Cypress
if ((window as any).Cypress) {
    // Export magic to use in our tests.
    (window as any).magic = magic;
    (window as any).inTestEnvironment = true;

    // Set configuration overrides on page load and expose config for on the fly configuration modification.
    const cyConfiguration = (window as any)._configTransformer;
    if (cyConfiguration) {
        cyConfiguration(config);
    }
}

// Set the initial theme to light or dark;
initializeTheme();

// Register tracking service version for use in this project
const trackingService = new TrackingServiceV2();
registerTrackingService(trackingService);

// Removes the splash screens
function removeSplashScreen() {
    document.body.classList.add('is-loaded');
}

// Uncomment the block of code
// if the PendingQueryTimeoutInMs default value needs to be overridden
/* if (window.inTestEnvironment) {
    // Overrides the PendingQueryTimeoutInMs default value
    // with the new value, provided in milliseconds
    window.queryBus.overridePendingQueryTimeoutInMs(11000);
} */
toolkit
    .bootstrap(config, COUNTRY_TO_JURISDICTION, defaultJurisdiction)
    .then(async () => {
        const ssoRegistration = initIncompleteSSORegistration();

        Vue.use(Fragment.Plugin);

        const [lang, i18n, router] = await Promise.all([
            languageQuery(),
            i18nQuery(),
            getRouter(),
        ]);

        await ssoRegistration;

        setHtmlLang(lang);

        const vm = new Vue({
            i18n,
            router,
            store,
            render: (h) => h(App),
        });

        // Wait for the router's initial navigation (including resolving the
        // matched route's async chunk, e.g. home-page) before mounting and
        // removing the splash screen. Otherwise the splash disappears and
        // reveals an app shell with an empty router-view while that chunk
        // is still downloading, then the routed content jumps in later.
        const onInitialNavigation = () => {
            vm.$mount('#app');
            removeSplashScreen();
            vm.$nextTick(() => {
                window.eventBus.publish(
                    MobileAppMessageTypes.WEBVIEW_LIFECYCLE,
                    { status: 'loaded' },
                );
            });
        };
        router.onReady(onInitialNavigation, (error) => {
            console.error(error);
            onInitialNavigation();
        });

        window.eventBus.subscribe<MobileAppMessage>(
            MobileAppMessageTypes.NAVIGATE_TO_FROM_MOBILE_APP,
            (event) => {
                const { route } = event.payload
                    ?.payload as MobileAppNavigateToMessage;
                return router.push(`/${route}`);
            },
        );

        links.registerLinkHandler((path) => {
            router.push(path).catch(() => null);
        });
    });
